import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAccess } from "@/hooks/useAccess";
import {
  BookOpen, Target, Trophy, BookmarkCheck, Timer, Flame,
  ArrowRight, ChevronRight, RefreshCw, Lock,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, tone = "navy", testId }) {
  const tones = {
    navy: "bg-[hsl(var(--brand-navy))]/10 text-[hsl(var(--brand-navy))]",
    orange: "bg-[hsl(var(--brand-orange))]/15 text-[hsl(var(--brand-orange))]",
    green: "bg-emerald-500/15 text-emerald-700",
    purple: "bg-violet-500/15 text-violet-700",
  };
  return (
    <Card className="border-border/70">
      <CardContent className="flex items-center gap-4 p-5">
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="font-display text-2xl font-bold" data-testid={testId}>{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { can } = useAccess();

  const load = async () => {
    setLoading(true);
    try {
      const [o, s] = await Promise.all([
        api.get("/analytics/overview"),
        api.get("/subjects"),
      ]);
      setOverview(o.data);
      setSubjects(s.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(var(--brand-orange))]">Dashboard</div>
          <h1 className="mt-1 font-display text-3xl font-bold">Welcome back, {user?.name?.split(" ")[0]}.</h1>
          <p className="mt-1 text-sm text-muted-foreground">Here is a snapshot of your preparation.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/analytics" data-testid="view-analytics-btn">View analytics</Link>
          </Button>
          <Button variant="outline" onClick={load} data-testid="refresh-dashboard-btn">
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          <Button asChild className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="start-quiz-btn">
            <Link to="/quiz/new">
              Start a quiz <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>

      {loading && !overview ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={BookOpen} label="Attempts" value={overview?.total_attempts ?? 0} testId="stat-attempts" />
          <StatCard icon={Target} label="Accuracy" value={`${overview?.overall_accuracy ?? 0}%`} tone="orange" testId="stat-accuracy" />
          <StatCard icon={Trophy} label="Best Score" value={`${overview?.best_score ?? 0}%`} tone="green" testId="stat-best" />
          <StatCard icon={BookmarkCheck} label="Bookmarks" value={overview?.bookmarks_count ?? 0} tone="purple" testId="stat-bookmarks" />
        </div>
      )}

      <div className="mt-10 grid gap-8 lg:grid-cols-3">
        {/* Subjects */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold">Subjects</h2>
            <Link to="/subjects" className="text-sm font-semibold text-[hsl(var(--brand-navy))] hover:underline">
              View all <ChevronRight className="inline h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {subjects.map((s) => {
              const stat = overview?.subject_accuracy?.find((x) => x.subject === s.name);
              return (
                <Card key={s.id} className="group border-border/70 transition hover:-translate-y-0.5 hover:shadow-md">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-display text-base font-bold">{s.name}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{s.question_count} questions · {s.topic_count} topics</div>
                      </div>
                      <div className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                        {s.code}
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="mb-1 flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Your accuracy</span>
                        <span className="font-semibold">{stat?.accuracy ?? 0}%</span>
                      </div>
                      <Progress value={stat?.accuracy ?? 0} className="h-2" />
                    </div>
                    <div className="mt-4 flex gap-2">
                      {!can(s.id) ? (
                        <Button size="sm" asChild className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid={`unlock-${s.code}-btn`}>
                          <Link to="/courses"><Lock className="mr-1.5 h-3.5 w-3.5" /> Unlock</Link>
                        </Button>
                      ) : (<>
                      <Button size="sm" variant="outline" asChild data-testid={`practice-${s.code}-btn`}>
                        <Link to={`/quiz/new?subject=${s.id}&mode=practice`}>Practice</Link>
                      </Button>
                      <Button size="sm" asChild className="bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy))]/90" data-testid={`mock-${s.code}-btn`}>
                        <Link to={`/quiz/new?subject=${s.id}&mode=mock`}>Mock</Link>
                      </Button>
                      </>)}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Right rail */}
        <div className="space-y-6">
          <Card className="border-border/70">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-base font-bold">Jump into a mode</h3>
                <Flame className="h-4 w-4 text-[hsl(var(--brand-orange))]" />
              </div>
              <div className="mt-4 grid gap-2">
                <Button asChild variant="outline" className="justify-between" data-testid="mode-practice-btn">
                  <Link to="/quiz/new?mode=practice">Practice <ChevronRight className="h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="justify-between" data-testid="mode-mock-btn">
                  <Link to="/quiz/new?mode=mock">Timed Mock <ChevronRight className="h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="justify-between" data-testid="mode-revision-btn">
                  <Link to="/quiz/new?mode=revision">Revision <ChevronRight className="h-4 w-4" /></Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/70">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-base font-bold">Recent attempts</h3>
                <Timer className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="mt-3 space-y-2">
                {(overview?.recent_attempts || []).slice(0, 5).map((a) => (
                  <Link
                    key={a.id}
                    to={`/quiz/${a.id}/result`}
                    className="block rounded-lg border border-border/70 px-3 py-2 text-sm hover:bg-secondary"
                    data-testid={`recent-attempt-${a.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium capitalize">{a.mode}</div>
                      <div className="font-mono text-xs">{a.percentage}%</div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {a.score}/{a.total} · {new Date(a.submitted_at).toLocaleDateString()}
                    </div>
                  </Link>
                ))}
                {(!overview?.recent_attempts || overview.recent_attempts.length === 0) && (
                  <div className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
                    No attempts yet. Start a practice quiz to see results here.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
